import React from "react"
import {
  HStack, Text, VStack,
} from "@chakra-ui/react"
import { ModalName } from "sharedTypes"
import Icon from "components/elements/Icon"
import Link from "components/elements/Link"
import useModal from "services/useModal"
import useGuestsStore from "services/useGuestsStore"

type Props = {
  query: string
  // True when chip filters / a segment / a guest list are active. Drives
  // the "Clear filters" affordance.
  filtersActive?: boolean
  // True specifically when chip rules are present in the URL (`?rules=`).
  // Chip rules are the only filter axis the API intersects with `search=`;
  // `segment_id=` and `group_id=` lose to search-wins (DIO-931). The
  // combined "X and the current filters" copy is only honest in the chip
  // case — for sidebar-identity + search we just say "no guests match X".
  chipsActive?: boolean
  onClearSearch: () => void
  onClearFilters?: () => void
  layout?: "stacked" | "inline"
}

const NoGuestsMatch = ({
  query, filtersActive, chipsActive, onClearSearch, onClearFilters, layout = "stacked",
}: Props) => {
  const showModal = useModal()
  const order = useGuestsStore((s) => s.order)

  const onAddGuest = () => {
    const field = order.startsWith("last_name") ? "lastName" : "firstName"
    showModal(ModalName.NewGuest, { [field]: query })
  }

  const message = (() => {
    if (query && chipsActive) {
      return `No guests match "${query}" and the current filters`
    }
    if (!query && filtersActive) return "No guests match the current filters"

    return `No guests match "${query}"`
  })()

  // Add-guest only makes sense when a SEARCH produced no matches — the
  // suggested guest name comes from the query string. With filters active
  // (no search or search + filters), there's no sensible name to suggest,
  // and the user's intent is "show me filtered results", not "create one".
  const showAddGuest = !!query && !filtersActive

  const Actions = layout === "inline" ? HStack : VStack

  return (
    <VStack spacing={5} px={8}>
      <Text fontSize="lg" color="gray.500" textAlign="center">
        {message}
      </Text>
      <Actions spacing={layout === "inline" ? 8 : 3}>
        {query && (
          <Link fontSize="lg" fontWeight="medium" onClick={onClearSearch}>
            Clear search
          </Link>
        )}
        {filtersActive && onClearFilters && (
          <Link fontSize="lg" fontWeight="medium" onClick={onClearFilters}>
            Clear filters
          </Link>
        )}
        {showAddGuest && (
          <Link
            fontSize="lg"
            fontWeight="medium"
            onClick={onAddGuest}
            display="inline-flex"
            alignItems="center"
          >
            <Icon icon="plus" mr={2} />
            Add guest &quot;{query}&quot;
          </Link>
        )}
      </Actions>
    </VStack>
  )
}

export default NoGuestsMatch
